import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router";

import type {
  DatesSetArg,
  DayCellMountArg,
  EventClickArg,
  EventContentArg,
  EventDropArg,
} from "@fullcalendar/core";
import enGbLocale from "@fullcalendar/core/locales/en-gb";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import type {
  DateClickArg,
  EventResizeDoneArg,
} from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { isBefore, startOfDay } from "date-fns";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  LogOutIcon,
  PencilIcon,
  Trash2Icon,
} from "lucide-react";

import { formatDate } from "simplydo/core/dates";
import { RichTextViewer } from "simplydo/react/richtext";
import { cx, styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Dropdown } from "simplydo/react/ui/dropdown";
import type { DropdownProps } from "simplydo/react/ui/dropdown";
import { H4 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import { useCurrentUser } from "utils/currentUser";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";

import GetCalendarLinkButton from "./GetCalendarLinkButton";
import {
  CalendarEventWithDates,
  CalendarResponseEvent,
  CellAction,
} from "./calendar-types";
import { getCalendarEventLocation } from "./calendarLocation";
import {
  allDayExclusiveEndToInclusive,
  fromAllDayStorageDate,
  getDefaultActionDate,
  getDefaultEventEndDate,
  isCalendarEventEditable,
  isSameAllDayDate,
  toAllDayStorageDate,
  toFullCalendarEvents,
} from "./fullCalendarUtils";

const CALENDAR_VIEW_OPTIONS = [
  { key: "dayGridMonth", text: "Month", value: "dayGridMonth" },
  { key: "timeGridWeek", text: "Week", value: "timeGridWeek" },
  { key: "timeGridDay", text: "Day", value: "timeGridDay" },
  { key: "listMonth", text: "Month list", value: "listMonth" },
] as const;

type CalendarViewType = (typeof CALENDAR_VIEW_OPTIONS)[number]["value"];

const CalendarWrapper = styled.div`
  position: relative;
  overflow: visible;
  width: 100%;
  .fc {
    --fc-border-color: #e0e0e0;
    --fc-today-bg-color: transparent;
    --fc-event-border-color: transparent;
    --fc-neutral-text-color: rgba(0, 0, 0, 0.45);
    --fc-button-bg-color: ${({ theme }) => theme.primaryColour};
    --fc-button-border-color: ${({ theme }) => theme.primaryColour};
    --fc-button-hover-bg-color: ${({ theme }) =>
      util.adjustColour(theme.primaryColour, -0.08)};
    --fc-button-hover-border-color: ${({ theme }) =>
      util.adjustColour(theme.primaryColour, -0.08)};
    --fc-button-active-bg-color: ${({ theme }) =>
      util.adjustColour(theme.primaryColour, -0.12)};
    --fc-button-active-border-color: ${({ theme }) =>
      util.adjustColour(theme.primaryColour, -0.12)};
    color: #000;
    font-size: 13px;
  }

  .fc .fc-col-header-cell-cushion,
  .fc .fc-daygrid-day-number,
  .fc .fc-timegrid-axis-cushion,
  .fc .fc-timegrid-slot-label-cushion {
    color: #000;
  }

  .fc .fc-day-other .fc-daygrid-day-number {
    color: rgba(0, 0, 0, 0.4);
  }

  .fc .fc-daygrid-day-frame {
    min-height: 8rem;
    height: 100%;
    box-sizing: border-box;
  }

  .fc .fc-daygrid-day.fc-day-today,
  .fc .fc-timegrid-col.fc-day-today {
    background-color: transparent;
  }

  .fc .fc-daygrid-day .fc-daygrid-day-number {
    border-radius: 20px;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
  }

  .fc .fc-daygrid-day.fc-day-today .fc-daygrid-day-number {
    background: #0b57d0;
    font-weight: 600;
    color: #fff;
  }

  .fc .fc-col-header-cell.fc-day-today {
    background: #e6f7ff;
  }

  .fc .fc-button {
    background: ${({ theme }) => theme.primaryColour};
    border-color: ${({ theme }) => theme.primaryColour};
    font-size: 0.9em;
    padding: 0.4em 0.65em;
  }

  .fc .fc-button:hover {
    background: ${({ theme }) => util.adjustColour(theme.primaryColour, -0.08)};
    border-color: ${({ theme }) =>
      util.adjustColour(theme.primaryColour, -0.08)};
  }

  .fc .fc-button-primary:not(:disabled).fc-button-active,
  .fc .fc-button-primary:not(:disabled):active {
    background: ${({ theme }) => util.adjustColour(theme.primaryColour, -0.12)};
    border-color: ${({ theme }) =>
      util.adjustColour(theme.primaryColour, -0.12)};
  }

  .fc .fc-daygrid-event {
    cursor: pointer;
  }

  .fc-event-select-content {
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    width: 100%;
    overflow: hidden;
  }

  .fc-event-select-content .fc-daygrid-event-dot {
    flex-shrink: 0;
    margin: 0;
  }

  .fc .fc-v-event .fc-event-select-content {
    flex-direction: column;
    align-items: flex-start;
  }

  &.is-selecting .fc .fc-v-event .fc-event-select-content {
    flex-direction: row;
    align-items: center;
  }

  .fc-event-check {
    display: none;
    flex-shrink: 0;
    width: 12px;
    height: 12px;
    border: 1.5px solid ${({ theme }) => theme.primaryColour};
    border-radius: 2px;
    box-sizing: border-box;
    background: transparent;
  }

  &.is-selecting .fc-event-check {
    display: block;
  }

  .fc-event.is-selected .fc-event-check {
    background: ${({ theme }) => theme.primaryColour};
    border-color: ${({ theme }) => theme.primaryColour};
  }

  .fc-event-select-content .fc-event-time,
  .fc-event-select-content .fc-event-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .fc-event-select-content .fc-event-title {
    flex: 1;
    min-width: 0;
  }

  .fc .fc-daygrid-block-event {
    padding-left: 3px;
  }

  .fc .fc-daygrid-dot-event.fc-event-start,
  .fc .fc-daygrid-block-event.fc-event-start {
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
  }

  .fc .fc-daygrid-dot-event.fc-event-start,
  .fc .fc-daygrid-block-event.fc-event-end {
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }

  .fc .fc-daygrid-event.fc-daygrid-block-event .fc-event-time {
    margin-right: 1px;
  }

  .fc .fc-daygrid-block-event:not(.fc-event-start) .fc-event-time,
  .fc .fc-daygrid-dot-event:not(.fc-event-start) .fc-event-time {
    display: none;
  }

  .fc .fc-daygrid-event-harness:not(.fc-daygrid-event-harness-abs) {
    max-width: 100%;
  }

  .fc .fc-daygrid-block-event,
  .fc .fc-daygrid-dot-event {
    line-height: 1.5em;
  }

  .fc .fc-daygrid-dot-event {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #000;
  }

  .fc .fc-daygrid-dot-event .fc-event-title,
  .fc .fc-daygrid-block-event .fc-event-title {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .fc .fc-daygrid-block-event .fc-event-title {
    font-weight: 600;
  }

  .fc .fc-v-event .fc-event-title {
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 600;
  }

  .fc .fc-bg-event {
    opacity: 0.25;
  }

  &.is-editable {
    .fc-daygrid-day-frame {
      position: relative;
      cursor: pointer;
      transition: background-color 0.15s ease;
      --fc-day-add-button-reserve: 15px;
      padding-bottom: var(--fc-day-add-button-reserve);
      box-sizing: border-box;
    }

    .fc .fc-daygrid-body-balanced .fc-daygrid-day-events {
      bottom: var(--fc-day-add-button-reserve);
    }

    .fc .fc-daygrid-body-unbalanced .fc-daygrid-day-events {
      margin-bottom: var(--fc-day-add-button-reserve);
    }

    .fc .fc-daygrid-day:hover,
    .fc .fc-timegrid-slot:hover {
      background-color: #f0f7fcaa;
    }

    .fc .fc-timegrid-body {
      cursor: pointer;
    }

    .fc-timegrid-cell-hover {
      position: absolute;
      display: none;
      background-color: #f0f7fc;
      pointer-events: none;
      z-index: 1;
      box-sizing: border-box;
    }

    .fc-day-add-button {
      position: absolute;
      bottom: 4px;
      right: 4px;
      z-index: 3;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      padding: 0;
      border: 1px solid ${({ theme }) => theme.primaryColour};
      border-radius: 4px;
      background: #fff;
      color: ${({ theme }) => theme.primaryColour};
      font-size: 16px;
      font-weight: bold;
      line-height: 1;
      cursor: pointer;
      opacity: 0;
      pointer-events: none;
      transition:
        opacity 0.15s ease,
        background-color 0.15s ease;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    }

    .fc-daygrid-day-frame:hover .fc-day-add-button,
    .fc-day-add-button.is-open,
    .fc-day-add-button:focus-visible {
      opacity: 1;
      pointer-events: auto;
    }

    .fc-day-add-button:hover {
      background: ${({ theme }) => theme.primaryColour};
      color: #fff;
      border-color: ${({ theme }) => theme.primaryColour};
    }
  }

  &.is-selecting {
    .fc-day-add-button {
      display: none;
    }

    .fc-daygrid-day-frame,
    .fc .fc-timegrid-body {
      cursor: default;
    }

    .fc-event.is-selectable {
      cursor: pointer;
    }

    .fc-event.is-selectable:not(.is-selected) {
      opacity: 0.55;
    }

    .fc-event:not(.is-selectable):not(.fc-bg-event) {
      opacity: 0.4;
      cursor: default;
    }

    .fc-event.is-selected {
      box-shadow:
        0 0 0 2px #fff,
        0 0 0 3px ${({ theme }) => theme.primaryColour};
      opacity: 1;
    }
  }
`;

const ToolbarStart = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ToolbarEnd = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-left: auto;
`;

const ToolbarTitle = styled.span`
  font-size: 1.25em;
  font-weight: 600;
  padding: 0 4px;
  white-space: nowrap;
`;

const ToolbarControls = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 16px;
`;

type DayActionMenuState = {
  date: Date;
  useClickedTime?: boolean;
  anchor?: HTMLElement;
  position?: { top: number; left: number };
};

const DAY_ACTION_MENU_WIDTH = 220;

const getDayActionMenuPosition = (menu: DayActionMenuState) => {
  if (menu.position) {
    return {
      top: menu.position.top + 4,
      left: Math.max(
        8,
        Math.min(
          menu.position.left - DAY_ACTION_MENU_WIDTH / 2,
          window.innerWidth - DAY_ACTION_MENU_WIDTH - 8,
        ),
      ),
    };
  }

  if (menu.anchor) {
    const rect = menu.anchor.getBoundingClientRect();
    return {
      top: rect.bottom + 4,
      left: Math.max(8, rect.right - DAY_ACTION_MENU_WIDTH),
    };
  }

  return null;
};

const DayActionMenu = styled.div<{ $top: number; $left: number }>`
  position: fixed;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  z-index: 1000;
  min-width: 220px;
  background: #fff;
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  padding: 4px 0;
`;

const DayActionMenuItem = styled.button`
  display: block;
  width: 100%;
  padding: 10px 16px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  font-weight: normal;
  color: #000;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

type FullCalendarViewProps = {
  events: CalendarResponseEvent[];
  updateEvent: (event: CalendarResponseEvent) => void;
  deleteEvent?: (eventId: string, eventType: string) => void;
  leaveEvent?: (eventId: string) => void;
  setEditingEvent: Dispatch<
    SetStateAction<Partial<CalendarEventWithDates> | null>
  >;
  editable: boolean;
  showMembers: boolean;
  cellActions?: Array<CellAction>;
  toolbarExtra?: ReactNode;
  calendarId?: string;
  isSelecting?: boolean;
  selectedEventIds?: string[];
  onToggleEventSelection?: (eventId: string) => void;
};

const DIRECT_EDIT_TYPES = new Set([
  "announcement",
  "open",
  "deadline",
  "assessmentDeadline",
]);

const FullCalendarView = ({
  events,
  updateEvent,
  deleteEvent,
  leaveEvent,
  setEditingEvent,
  editable,
  showMembers,
  cellActions,
  toolbarExtra,
  calendarId,
  isSelecting = false,
  selectedEventIds,
  onToggleEventSelection,
}: FullCalendarViewProps) => {
  const user = useCurrentUser();
  const location = useLocation();
  const navigate = useNavigate();
  const calendarRef = useRef<FullCalendar>(null);
  const calendarWrapperRef = useRef<HTMLDivElement>(null);
  const [currentView, setCurrentView] =
    useState<CalendarViewType>("dayGridMonth");
  const { basePath, eventId } = getCalendarEventLocation(location.pathname);
  const [toolbarTitle, setToolbarTitle] = useState("");
  const viewingEvent = useMemo(() => {
    if (!eventId) {
      return null;
    }
    return events.find((event) => event.id === eventId) ?? null;
  }, [eventId, events]);
  const [dayActionMenu, setDayActionMenu] = useState<DayActionMenuState | null>(
    null,
  );
  const dayActionMenuRef = useRef<HTMLDivElement>(null);

  const selectedEventIdSet = useMemo(
    () => new Set(selectedEventIds ?? []),
    [selectedEventIds],
  );

  const canInteractWithCalendar = editable && !isSelecting;

  const fcEvents = useMemo(
    () =>
      toFullCalendarEvents(events, editable, {
        isSelecting,
        selectedEventIds: selectedEventIdSet,
      }),
    [editable, events, isSelecting, selectedEventIdSet],
  );

  const closeViewingEvent = () => {
    if (eventId) {
      navigate(basePath);
    }
  };

  const handleEventClick = (info: EventClickArg) => {
    if (info.event.display === "background") {
      return;
    }
    const sourceEvent = info.event.extendedProps.sourceEvent as
      | CalendarResponseEvent
      | undefined;
    if (!sourceEvent) {
      return;
    }
    if (isSelecting) {
      if (sourceEvent.id && isCalendarEventEditable(sourceEvent, true)) {
        onToggleEventSelection?.(sourceEvent.id);
      }
      return;
    }
    if (sourceEvent.type && DIRECT_EDIT_TYPES.has(sourceEvent.type)) {
      setEditingEvent(sourceEvent as Partial<CalendarEventWithDates>);
      return;
    }
    if (!sourceEvent.id) {
      return;
    }
    navigate(`${basePath}/events/${sourceEvent.id}`);
  };

  const renderEventContent = useCallback((arg: EventContentArg) => {
    if (arg.event.display === "background") {
      return true;
    }
    const sourceEvent = arg.event.extendedProps.sourceEvent as
      | CalendarResponseEvent
      | undefined;
    const selectable =
      !!sourceEvent && isCalendarEventEditable(sourceEvent, true);
    const isListView = arg.view.type.startsWith("list");
    const showDot = arg.view.type.startsWith("dayGrid") && !arg.event.allDay;
    return (
      <div className="fc-event-select-content">
        {selectable ? <span className="fc-event-check" aria-hidden /> : null}
        {showDot ? (
          <div
            className="fc-daygrid-event-dot"
            style={{ borderColor: arg.borderColor || arg.backgroundColor }}
          />
        ) : null}
        {!isListView && arg.timeText ? (
          <span className="fc-event-time">{arg.timeText}</span>
        ) : null}
        <span className="fc-event-title fc-sticky">{arg.event.title}</span>
      </div>
    );
  }, []);

  const handleDateClick = (info: DateClickArg) => {
    if (!canInteractWithCalendar || info.view.type === "dayGridMonth") {
      return;
    }
    openDayActionMenu(info.date, {
      position: { top: info.jsEvent.clientY, left: info.jsEvent.clientX },
      useClickedTime: true,
    });
  };

  const openDayActionMenu = useCallback(
    (
      date: Date,
      options: {
        anchor?: HTMLElement;
        position?: { top: number; left: number };
        useClickedTime?: boolean;
      },
    ) => {
      if (isSelecting) {
        return;
      }
      setDayActionMenu((prev) => {
        if (options.anchor && prev?.anchor === options.anchor) {
          options.anchor.classList.remove("is-open");
          return null;
        }
        prev?.anchor?.classList.remove("is-open");
        options.anchor?.classList.add("is-open");
        return { date, ...options };
      });
    },
    [isSelecting],
  );

  const closeDayActionMenu = useCallback(() => {
    setDayActionMenu((prev) => {
      prev?.anchor?.classList.remove("is-open");
      return null;
    });
  }, []);

  useEffect(() => {
    if (!dayActionMenu) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        (dayActionMenu.anchor && dayActionMenu.anchor.contains(target)) ||
        dayActionMenuRef.current?.contains(target)
      ) {
        return;
      }
      closeDayActionMenu();
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeDayActionMenu();
      }
    };

    const handleScroll = () => {
      closeDayActionMenu();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    document.addEventListener("scroll", handleScroll, true);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [closeDayActionMenu, dayActionMenu]);

  const handleDayCellDidMount = useCallback(
    (info: DayCellMountArg) => {
      if (!editable) {
        return;
      }

      const frame =
        info.el.querySelector<HTMLElement>(".fc-daygrid-day-frame") ?? info.el;
      if (frame.querySelector(".fc-day-add-button")) {
        return;
      }

      const addButton = document.createElement("button");
      addButton.type = "button";
      addButton.className = "fc-day-add-button";
      addButton.setAttribute("aria-label", "Add to calendar");
      addButton.title = "Add to calendar";
      addButton.textContent = "+";
      addButton.addEventListener("click", (event) => {
        event.stopPropagation();
        openDayActionMenu(info.date, { anchor: addButton });
      });
      frame.appendChild(addButton);
    },
    [editable, openDayActionMenu],
  );

  const handleDayCellWillUnmount = useCallback((info: DayCellMountArg) => {
    info.el.querySelector(".fc-day-add-button")?.remove();
  }, []);

  const dayCellClassNames = useCallback(() => {
    return editable ? ["fc-day-clickable"] : [];
  }, [editable]);

  const applyDateUpdate = (
    sourceEvent: CalendarResponseEvent,
    start: Date | null,
    end: Date | null | undefined,
    allDay: boolean,
  ) => {
    if (allDay && start) {
      const inclusiveEnd = end
        ? allDayExclusiveEndToInclusive(end)
        : toAllDayStorageDate(start);
      updateEvent({
        ...sourceEvent,
        allDay: true,
        from: toAllDayStorageDate(start).toISOString(),
        to: inclusiveEnd.toISOString(),
      });
      return;
    }
    updateEvent({
      ...sourceEvent,
      allDay: false,
      from: start?.toISOString() ?? sourceEvent.from,
      to: end?.toISOString() ?? sourceEvent.to,
    });
  };

  const handleEventDrop = (info: EventDropArg) => {
    const sourceEvent = info.event.extendedProps.sourceEvent as
      | CalendarResponseEvent
      | undefined;
    if (!sourceEvent) {
      info.revert();
      return;
    }
    applyDateUpdate(
      sourceEvent,
      info.event.start,
      info.event.end,
      info.event.allDay,
    );
  };

  const handleEventResize = (info: EventResizeDoneArg) => {
    const sourceEvent = info.event.extendedProps.sourceEvent as
      | CalendarResponseEvent
      | undefined;
    if (!sourceEvent) {
      info.revert();
      return;
    }
    applyDateUpdate(
      sourceEvent,
      info.event.start,
      info.event.end,
      info.event.allDay,
    );
  };

  const createEventAtDate = (dayDate: Date, useClickedTime = false) => {
    if (!user) {
      return;
    }
    closeDayActionMenu();
    const from = getDefaultActionDate(dayDate, useClickedTime);
    setEditingEvent({
      type: "event",
      from,
      to: getDefaultEventEndDate(from),
      color: "#3f88d2",
      members: [user._id],
      ownerMembers: [{ _id: user._id, profile: user.profile }],
      organiserName: user.profile?.fullName ?? "",
      organiserEmail: user.emails[0]?.address ?? "",
      notifications: [{ value: 10, unit: "minutes" }],
    });
  };

  const viewingEventFrom = viewingEvent?.from
    ? new Date(viewingEvent.from)
    : null;
  const viewingEventTo = viewingEvent?.to
    ? new Date(viewingEvent.to)
    : viewingEventFrom;
  const isViewingAllDay = !!viewingEvent?.allDay;
  const hasSeparateEndDate =
    viewingEventFrom &&
    viewingEventTo &&
    (isViewingAllDay
      ? !isSameAllDayDate(viewingEventFrom, viewingEventTo)
      : viewingEventFrom.toDateString() !== viewingEventTo.toDateString());
  const dateFormat = isViewingAllDay ? "MMM dd, yyyy" : "MMM dd, HH:mm";
  const formatViewingDate = (date: Date) => {
    if (isViewingAllDay) {
      return formatDate(fromAllDayStorageDate(date), "MMM dd, yyyy");
    }
    return formatDate(date, dateFormat);
  };

  const handleViewChange = (
    _event: React.SyntheticEvent<HTMLElement>,
    data: DropdownProps,
  ) => {
    if (typeof data.value !== "string") {
      return;
    }
    const view = data.value as CalendarViewType;
    setCurrentView(view);
    calendarRef.current?.getApi().changeView(view);
  };

  const handleDatesSet = (arg: DatesSetArg) => {
    setToolbarTitle(arg.view.title);
  };

  const goToPrev = () => {
    calendarRef.current?.getApi().prev();
  };

  const goToNext = () => {
    calendarRef.current?.getApi().next();
  };

  const goToToday = () => {
    calendarRef.current?.getApi().today();
  };

  const visibleCellActions = useMemo(() => {
    if (!dayActionMenu || !cellActions) {
      return [];
    }
    return cellActions.filter((action) => {
      if (!action.onlyVisibleInRange) {
        return true;
      }
      return !isBefore(startOfDay(dayActionMenu.date), startOfDay(new Date()));
    });
  }, [cellActions, dayActionMenu]);

  const dayActionMenuPosition = dayActionMenu
    ? getDayActionMenuPosition(dayActionMenu)
    : null;

  return (
    <CalendarWrapper
      ref={calendarWrapperRef}
      className={cx(editable && "is-editable", isSelecting && "is-selecting")}
    >
      <ToolbarControls>
        <ToolbarStart>
          <Button size="small" variant="outline" onClick={goToToday}>
            Today
          </Button>
          <Button
            size="small"
            variant="ghost"
            onClick={goToPrev}
            aria-label="Previous"
          >
            <Icon icon={ChevronLeftIcon} />
          </Button>
          <Button
            size="small"
            variant="ghost"
            onClick={goToNext}
            aria-label="Next"
          >
            <Icon icon={ChevronRightIcon} />
          </Button>
          {toolbarTitle ? <ToolbarTitle>{toolbarTitle}</ToolbarTitle> : null}
        </ToolbarStart>
        <ToolbarEnd>
          {calendarId ? (
            <GetCalendarLinkButton
              calendarId={calendarId}
              variant="outline"
              size="small"
            />
          ) : null}
          {toolbarExtra}
          <Dropdown
            variant="outline"
            options={[...CALENDAR_VIEW_OPTIONS]}
            value={currentView}
            onChange={handleViewChange}
            aria-label="Calendar view"
          />
        </ToolbarEnd>
      </ToolbarControls>
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
        locale={enGbLocale}
        initialView="dayGridMonth"
        firstDay={1}
        headerToolbar={false}
        datesSet={handleDatesSet}
        eventTimeFormat={{
          hour: "numeric",
          minute: "2-digit",
          meridiem: false,
          hour12: false,
        }}
        events={fcEvents}
        editable={canInteractWithCalendar}
        eventStartEditable={canInteractWithCalendar}
        eventDurationEditable={canInteractWithCalendar}
        fixedWeekCount={false}
        height="auto"
        views={{
          dayGridMonth: { aspectRatio: 1 },
          timeGridWeek: {
            height: 600,
            dayHeaderFormat: {
              weekday: "short",
              day: "numeric",
              month: "numeric",
              omitCommas: true,
            },
          },
          timeGridDay: {
            height: 600,
            dayHeaderFormat: {
              weekday: "short",
              day: "numeric",
              month: "numeric",
              omitCommas: true,
            },
          },
        }}
        nowIndicator
        dayCellClassNames={dayCellClassNames}
        dayCellDidMount={handleDayCellDidMount}
        dayCellWillUnmount={handleDayCellWillUnmount}
        dateClick={handleDateClick}
        eventClick={handleEventClick}
        eventContent={renderEventContent}
        eventDrop={handleEventDrop}
        eventResize={handleEventResize}
        dayMaxEvents={4}
        moreLinkClick="popover"
      />

      {!isSelecting && dayActionMenu && dayActionMenuPosition
        ? createPortal(
            <DayActionMenu
              ref={dayActionMenuRef}
              $top={dayActionMenuPosition.top}
              $left={dayActionMenuPosition.left}
              role="menu"
              aria-label="Add to calendar"
            >
              <DayActionMenuItem
                type="button"
                role="menuitem"
                onClick={() =>
                  createEventAtDate(
                    dayActionMenu.date,
                    !!dayActionMenu.useClickedTime,
                  )
                }
              >
                Add an event
              </DayActionMenuItem>
              {visibleCellActions.map((action) => (
                <DayActionMenuItem
                  key={action.name}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    const eventData = action.action(
                      getDefaultActionDate(
                        dayActionMenu.date,
                        !!dayActionMenu.useClickedTime,
                      ).toISOString(),
                    );
                    closeDayActionMenu();
                    setEditingEvent(
                      eventData as Partial<CalendarEventWithDates>,
                    );
                  }}
                >
                  {action.name}
                </DayActionMenuItem>
              ))}
            </DayActionMenu>,
            document.body,
          )
        : null}

      <Modal open={!!viewingEvent && !isSelecting} onClose={closeViewingEvent}>
        <ModalHeader>{viewingEvent?.title}</ModalHeader>
        <ModalContent scrolling>
          {viewingEventFrom ? (
            <>
              <H4>Date</H4>
              {hasSeparateEndDate
                ? `${formatViewingDate(viewingEventFrom)} - ${formatViewingDate(viewingEventTo!)}`
                : formatViewingDate(viewingEventFrom)}
            </>
          ) : null}
          {viewingEvent?.description ? (
            <>
              <H4>Description</H4>
              <RichTextViewer value={viewingEvent.description} />
            </>
          ) : null}
          {showMembers &&
          viewingEvent?.ownerMembers &&
          viewingEvent.ownerMembers.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Members</TableHeaderCell>
                  <TableHeaderCell />
                </TableRow>
              </TableHeader>
              <TableBody>
                {viewingEvent.ownerMembers.map((member) => (
                  <TableRow key={member._id}>
                    <TableCell>
                      <UserChip user={member as never} compact />
                    </TableCell>
                    <TableCell collapsing>
                      {leaveEvent && member._id === user?._id ? (
                        <Button
                          onClick={() => {
                            leaveEvent(viewingEvent.id!);
                            closeViewingEvent();
                          }}
                          variant="outline"
                        >
                          <Icon icon={LogOutIcon} />
                          Leave
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </ModalContent>
        <ModalActions>
          {editable && viewingEvent && !viewingEvent.isGenerated ? (
            <>
              {deleteEvent && viewingEvent.id ? (
                <Button
                  onClick={() => {
                    deleteEvent(viewingEvent.id!, viewingEvent.type || "event");
                    closeViewingEvent();
                  }}
                  variant="outline"
                >
                  <Icon icon={Trash2Icon} />
                </Button>
              ) : null}
              <Button
                onClick={() => {
                  setEditingEvent(
                    viewingEvent as Partial<CalendarEventWithDates>,
                  );
                  closeViewingEvent();
                }}
              >
                <Icon icon={PencilIcon} />
                Edit
              </Button>
            </>
          ) : null}
          <Button onClick={closeViewingEvent}>Close</Button>
        </ModalActions>
      </Modal>
    </CalendarWrapper>
  );
};

export default FullCalendarView;
